/**
 * @fileoverview Repository factory and exports
 */

import { DataRepository } from './base/DataRepository';
import { OrdenProduccionRepository } from './OrdenProduccionRepository';
import { ClienteRepository } from './ClienteRepository';
import { ProductoRepository } from './ProductoRepository';
import { TintaRepository } from './TintaRepository';
import { ParametrosTintaRepository } from './ParametrosTintaRepository';
import { ImpresionTablaRepository } from './ImpresionTablaRepository';
import { ProcesoProveedorRepository } from './ProcesoProveedorRepository';
import { SolicitudInventarioRepository } from './SolicitudInventarioRepository';
import { ReservaInventarioRepository } from './ReservaInventarioRepository';
import { UnidadRepository } from './UnidadRepository';
import { InventarioMaterialesRepository } from './InventarioMaterialesRepository';
import { LogInventarioMaterialesRepository } from './LogInventarioMaterialesRepository';
import { CentroCostoRepository } from './CentroCostoRepository';
import { UsuarioRepository } from './UsuarioRepository';
import { LogPlanCentroCostoRepository } from './LogPlanCentroCostoRepository';
import { ProductoInventarioRepository } from './ProductoInventarioRepository';
import type { BaseRecord } from '../types/common/PocketBaseTypes';

/**
 * Registry of repository classes
 */
type RepositoryClass<T extends BaseRecord> = new (...args: any[]) => DataRepository<T>;

/**
 * Repository instances cache
 */
const repositoryInstances = new Map<string, DataRepository<any>>();

/**
 * Repository class registry
 */
const repositoryClasses = new Map<string, RepositoryClass<any>>();

/**
 * Factory for managing repository instances
 */
export class RepositoryFactory {
  /**
   * Register a repository class
   */
  static registerRepository<T extends BaseRecord>(
    name: string, 
    repositoryClass: RepositoryClass<T>
  ): void {
    repositoryClasses.set(name, repositoryClass);
  }

  /**
   * Get repository instance (singleton pattern)
   */
  static getRepository<T extends BaseRecord>(name: string): DataRepository<T> {
    // Check if instance already exists
    if (repositoryInstances.has(name)) {
      return repositoryInstances.get(name)!;
    }

    // Check if repository class is registered
    const RepositoryClass = repositoryClasses.get(name);
    if (!RepositoryClass) {
      throw new Error(`Repository '${name}' is not registered. Available repositories: ${Array.from(repositoryClasses.keys()).join(', ')}`);
    }

    // Create new instance
    const instance = new RepositoryClass();
    repositoryInstances.set(name, instance);
    
    return instance;
  }

  /**
   * Check if repository is registered
   */
  static isRegistered(name: string): boolean {
    return repositoryClasses.has(name);
  }

  /**
   * Get list of registered repository names
   */
  static getRegisteredRepositories(): string[] {
    return Array.from(repositoryClasses.keys());
  }

  /**
   * Clear all instances (useful for testing)
   */
  static clearInstances(): void {
    repositoryInstances.clear();
  }

  /**
   * Clear all registrations (useful for testing)
   */
  static clearRegistrations(): void {
    repositoryClasses.clear();
    repositoryInstances.clear();
  }

  /**
   * Get instance count (useful for monitoring)
   */
  static getInstanceCount(): number {
    return repositoryInstances.size;
  }
}

/**
 * Helper function to create and register a repository
 */
export function createRepository<T extends BaseRecord>(
  name: string,
  repositoryClass: RepositoryClass<T>
): DataRepository<T> {
  RepositoryFactory.registerRepository(name, repositoryClass);
  return RepositoryFactory.getRepository<T>(name);
}

/**
 * Helper function for type-safe repository access
 */
export function useRepository<T extends BaseRecord>(name: string): DataRepository<T> {
  return RepositoryFactory.getRepository<T>(name);
}

// Export base classes and types
export { DataRepository } from './base/DataRepository';
export { OrdenProduccionRepository } from './OrdenProduccionRepository';
export { ClienteRepository } from './ClienteRepository';
export { ProductoRepository } from './ProductoRepository';
export { TintaRepository } from './TintaRepository';
export { ParametrosTintaRepository } from './ParametrosTintaRepository';
export { ImpresionTablaRepository } from './ImpresionTablaRepository';
export { ProcesoProveedorRepository } from './ProcesoProveedorRepository';
export { SolicitudInventarioRepository } from './SolicitudInventarioRepository';
export { ReservaInventarioRepository } from './ReservaInventarioRepository';
export { UnidadRepository } from './UnidadRepository';
export { InventarioMaterialesRepository } from './InventarioMaterialesRepository';
export { LogInventarioMaterialesRepository } from './LogInventarioMaterialesRepository';
export { CentroCostoRepository } from './CentroCostoRepository';
export { UsuarioRepository } from './UsuarioRepository';
export { LogPlanCentroCostoRepository } from './LogPlanCentroCostoRepository';
export { RelatedDataHelper } from './helpers/RelatedDataHelper';
export * from './base/RepositoryConfig';
export * from '../types/common/PocketBaseTypes';

// Register built-in repositories
RepositoryFactory.registerRepository('orden_produccion', OrdenProduccionRepository);
RepositoryFactory.registerRepository('cliente', ClienteRepository);
RepositoryFactory.registerRepository('producto', ProductoRepository);
RepositoryFactory.registerRepository('tinta', TintaRepository);
RepositoryFactory.registerRepository('parametros_tinta', ParametrosTintaRepository);
RepositoryFactory.registerRepository('impresion_tabla', ImpresionTablaRepository);
RepositoryFactory.registerRepository('proceso_proveedor', ProcesoProveedorRepository);
RepositoryFactory.registerRepository('solicitud_inventario', SolicitudInventarioRepository);
RepositoryFactory.registerRepository('reserva_inventario', ReservaInventarioRepository);
RepositoryFactory.registerRepository('unidad', UnidadRepository);
RepositoryFactory.registerRepository('inventario_materiales', InventarioMaterialesRepository);
RepositoryFactory.registerRepository('log_inventario_materiales', LogInventarioMaterialesRepository);
RepositoryFactory.registerRepository('centro_costo', CentroCostoRepository);
RepositoryFactory.registerRepository('users', UsuarioRepository);
RepositoryFactory.registerRepository('log_plan_centro_costo', LogPlanCentroCostoRepository);
RepositoryFactory.registerRepository('producto_inventario', ProductoInventarioRepository);
